import { Link } from "react-router-dom";
import { Facebook, Instagram, Phone, Mail, MapPin, Star } from "lucide-react";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import TranslatedText from "@/components/TranslatedText";
import { useEffect, useRef, useState } from "react";

const Footer = () => {
  const { siteConfig } = useSiteConfig();
  const footerRef = useRef<HTMLElement>(null);
  const [widgetsVisible, setWidgetsVisible] = useState(false);

  // Only load third-party widgets once the footer approaches the viewport,
  // so they never compete with the initial render (LCP/TBT).
  useEffect(() => {
    const el = footerRef.current;
    if (!el || widgetsVisible) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setWidgetsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [widgetsVisible]);

  useEffect(() => {
    if (!widgetsVisible) return;

    // Load TripAdvisor script
    const tripAdvisorScript = document.createElement('script');
    tripAdvisorScript.src = 'https://www.jscache.com/wejs?wtype=selfserveprop&uniq=486&locationId=33445692&lang=en_US&rating=true&nreviews=5&writereviewlink=true&popIdx=true&iswide=false&border=true&display_version=2';
    tripAdvisorScript.async = true;
    document.body.appendChild(tripAdvisorScript);

    return () => {
      if (document.body.contains(tripAdvisorScript)) {
        document.body.removeChild(tripAdvisorScript);
      }
    };
  }, [widgetsVisible]);
  
  return (
    <footer ref={footerRef} className="border-t border-secondary-foreground/10 bg-secondary text-secondary-foreground">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <img 
                src={siteConfig?.logo_footer_url || "https://carpeta.tecnosync.com/uploads/68a62ded09aec_1755721197.png"} 
                alt="Ride Punta Cana" 
                width={160}
                height={40}
                loading="lazy"
                decoding="async"
                className="h-10 w-auto"
              />
            </div>
            <p className="text-sm text-secondary-foreground/80">
              <TranslatedText>Ride Punta Cana - Experience the ultimate adventure in Punta Cana with our premium buggy and ATV tours through jungle trails and hidden cenotes.</TranslatedText>
            </p>
            <div className="flex space-x-4">
              <Facebook className="h-5 w-5 cursor-pointer hover:text-primary transition-colors" />
              <Instagram className="h-5 w-5 cursor-pointer hover:text-primary transition-colors" />
              <Star className="h-5 w-5 cursor-pointer hover:text-primary transition-colors" />
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="font-display text-2xl text-secondary-foreground"><TranslatedText>Quick Links</TranslatedText></h3>
            <div className="space-y-2 text-sm">
              <Link to="/" className="block hover:text-primary transition-colors"><TranslatedText>Home</TranslatedText></Link>
              <Link to="/about" className="block hover:text-primary transition-colors"><TranslatedText>About Us</TranslatedText></Link>
              <Link to="/services" className="block hover:text-primary transition-colors"><TranslatedText>Buggy Services</TranslatedText></Link>
              <Link to="/contact" className="block hover:text-primary transition-colors"><TranslatedText>Contact</TranslatedText></Link>
            </div>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="font-display text-2xl text-secondary-foreground"><TranslatedText>Contact Info</TranslatedText></h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center space-x-2">
                <Phone className="h-4 w-4 text-primary" />
                <span>{siteConfig?.phone || "+1 (809) 555-0123"}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-primary" />
                <span>{siteConfig?.email || "info@ridepuntacana.com"}</span>
              </div>
              <div className="flex items-start space-x-2">
                <MapPin className="h-4 w-4 text-primary mt-1" />
                <span>{siteConfig?.address || "Bavaro, Punta Cana\nDominican Republic"}</span>
              </div>
            </div>
          </div>

          {/* TripAdvisor Widget */}
          <div className="space-y-4">
            <h3 className="font-display text-2xl text-secondary-foreground"><TranslatedText>Reviews</TranslatedText></h3>
            <div id="TA_selfserveprop486" className="TA_selfserveprop">
              <ul id="v60cby5wP" className="TA_links QUlgMQS">
                <li id="3TQfVrN" className="fMvKhhRfg1u">
                  <a 
                    target="_blank" 
                    rel="noopener noreferrer"
                    href="https://www.tripadvisor.com/Attraction_Review-g25296422-d33445692-Reviews-Ride_Punta_Cana-Macao_Punta_Cana_La_Altagracia_Province_Dominican_Republic.html"
                  >
                    <img 
                      src="https://www.tripadvisor.com/img/cdsi/img2/branding/v2/Tripadvisor_lockup_horizontal_secondary_registered-11900-2.svg" 
                      alt="TripAdvisor"
                      width={150}
                      height={26}
                      loading="lazy"
                      decoding="async"
                      className="max-w-[150px]"
                    />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-secondary-foreground/20 mt-8 pt-8 text-center text-sm text-secondary-foreground/80">
          <p><TranslatedText>&copy; 2024 Ride Punta Cana. All rights reserved. | Adventure Tours in Dominican Republic</TranslatedText></p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
